import { useEffect, useState } from 'react'
import { listCostLayers } from '../../api/inventory'
import { getLocations } from '../../api/master'
import { fmtMoney } from '../../utils/format'
import { BackLink, DocHeader } from '../../components/PageParts'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'

export default function CostLayers() {
  const [rows, setRows] = useState([])
  const [sku, setSku] = useState('')
  const [locationId, setLocationId] = useState('')
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => { getLocations().then(setLocations).catch(() => setLocations([])) }, [])

  const refresh = async () => {
    setLoading(true); setErr('')
    try {
      const params = {}
      if (sku.trim()) params.code = sku.trim()
      if (locationId) params.location_id = Number(locationId)
      const data = await listCostLayers(params)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      setErr(e?.message || 'Failed to load layers')
      setRows([])
    }
    setLoading(false)
  }
  useEffect(() => { refresh() }, [])

  const exportCSV = () => {
    const header = ['Layer ID', 'Product ID', 'SKU', 'Name', 'Location', 'Qty Remaining', 'Final Unit Cost', 'Value', 'Reason', 'Created At']
    const lines = rows.map(r => [r.layer_id, r.product_id, r.sku, r.name, r.location_name || r.location_id || '', r.qty_remaining, r.unit_cost, r.value, r.reason || '', r.created_at])
    const csv = [header, ...lines].map(a => a.map(x => (x == null ? '' : String(x))).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'product_cost_layers.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const totalQty = rows.reduce((s, r) => s + Number(r.qty_remaining || 0), 0)
  const totalVal = rows.reduce((s, r) => s + Number(r.value || 0), 0)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/inventory/stock" label="Stock" />}
        title="Cost batches"
        meta="Every batch still in stock with what it cost. The oldest are sold first (FIFO)."
        actions={<>
          <Button variant="ghost" size="sm" onClick={exportCSV}>Export CSV</Button>
          <Button variant="primary" size="sm" loading={loading} onClick={refresh}>Reload</Button>
        </>}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 160px auto 200px auto 1fr', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <label>SKU/Barcode</label>
        <input value={sku} onChange={e => setSku(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') refresh() }} placeholder="P001 or barcode" />
        <label>Location</label>
        <select value={locationId} onChange={e => setLocationId(e.target.value)}>
          <option value="">All</option>
          {(locations || []).map(l => (<option key={l.location_id} value={l.location_id}>{l.location_id}: {l.name}</option>))}
        </select>
        <span />
        <Button variant="primary" size="sm" onClick={refresh}>Apply Filters</Button>
      </div>
      {err && <ErrorBanner message={err} />}
      <div className="scroll-x" style={{ marginTop: 10 }}>
        <table className="table ds-table-pro">
          <thead>
            <tr>
              <th>Layer ID</th>
              <th>Product ID</th>
              <th>SKU</th>
              <th>Name</th>
              <th>Location</th>
              <th style={{ textAlign: 'right' }}>Qty Remaining</th>
              <th style={{ textAlign: 'right' }}>Final Unit Cost</th>
              <th style={{ textAlign: 'right' }}>Value</th>
              <th>Reason</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.layer_id}</td>
                <td>{r.product_id}</td>
                <td>{r.sku}</td>
                <td>{r.name}</td>
                <td>{r.location_name || r.location_id || ''}</td>
                <td style={{ textAlign: 'right' }}>{Number(r.qty_remaining || 0).toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(r.unit_cost)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(r.value)}</td>
                <td>{(r.reason || '') ? (<span className="tag" title={r.source_ref || ''}>{r.reason}</span>) : ''}</td>
                <td>{r.created_at}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}></td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(totalQty).toLocaleString()}</td>
              <td></td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(totalVal)}</td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
